import React, { Suspense, lazy } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import Navbar from '@/components/layout/Navbar';
import PushNotificationManager from '@/components/PushNotificationManager';
import { Toaster } from 'sonner';
import CookieConsent from '@/components/CookieConsent';

const Landing = lazy(() => import('@/pages/Landing'));
const Login = lazy(() => import('@/pages/Login'));
const Register = lazy(() => import('@/pages/Register'));
const ForgotPassword = lazy(() => import('@/pages/ForgotPassword'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const ReportUpload = lazy(() => import('@/pages/ReportUpload'));
const ReportDetail = lazy(() => import('@/pages/ReportDetail'));
const ReportsList = lazy(() => import('@/pages/ReportsList'));
const Chat = lazy(() => import('@/pages/Chat'));
const ReportComparison = lazy(() => import('@/pages/ReportComparison'));
const HealthTrends = lazy(() => import('@/pages/HealthTrends'));
const SmartAlerts = lazy(() => import('@/pages/SmartAlerts'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const AdminPanel = lazy(() => import('@/pages/AdminPanel'));
const Settings = lazy(() => import('@/pages/Settings'));
const Disclaimer = lazy(() => import('@/pages/Disclaimer'));
const Privacy = lazy(() => import('@/pages/Privacy'));
const Terms = lazy(() => import('@/pages/Terms'));
const SharedReport = lazy(() => import('@/pages/SharedReport'));
const ResetPassword = lazy(() => import('@/pages/ResetPassword'));
const HowItWorks = lazy(() => import('@/pages/HowItWorks'));
const SupportedReports = lazy(() => import('@/pages/SupportedReports'));
const Contact = lazy(() => import('@/pages/Contact'));
const FAQ = lazy(() => import('@/pages/FAQ'));
const NotFound = lazy(() => import('@/pages/NotFound'));
const Maintenance = lazy(() => import('@/pages/Maintenance'));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="w-6 h-6 border-2 border-[#2C4A3B] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

function CatchAllRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? '/dashboard' : '/login'} replace />;
}

function AppLayout() {
  const location = useLocation();
  const publicPaths = ['/', '/login', '/register', '/forgot-password', '/reset-password', '/disclaimer', '/privacy', '/terms', '/how-it-works', '/supported-reports', '/contact', '/faq', '/maintenance'];
  const showNav = !publicPaths.includes(location.pathname) && !location.pathname.startsWith('/shared/');

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      {showNav && <Navbar />}
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/disclaimer" element={<Disclaimer />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/shared/:token" element={<SharedReport />} />
          <Route path="/how-it-works" element={<HowItWorks />} />
          <Route path="/supported-reports" element={<SupportedReports />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/maintenance" element={<Maintenance />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/upload" element={<ProtectedRoute><ReportUpload /></ProtectedRoute>} />
          <Route path="/reports" element={<ProtectedRoute><ReportsList /></ProtectedRoute>} />
          <Route path="/reports/:id" element={<ProtectedRoute><ReportDetail /></ProtectedRoute>} />
          <Route path="/chat/:reportId" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
          <Route path="/compare" element={<ProtectedRoute><ReportComparison /></ProtectedRoute>} />
          <Route path="/trends" element={<ProtectedRoute><HealthTrends /></ProtectedRoute>} />
          <Route path="/alerts" element={<ProtectedRoute><SmartAlerts /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminPanel /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PushNotificationManager />
        <Toaster position="top-right" richColors />
        <CookieConsent />
        <AppLayout />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
